
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Box, Grid, Card, CardContent, Typography, Chip,
    IconButton, Stack, Avatar
} from '@mui/material';
import {
    PlayArrow,
    InfoOutlined,
    ErrorOutline,
    HistoryToggleOff,
    MenuBook,
    ChatBubbleOutline
} from '@mui/icons-material';
import { AlertCircle, CalendarDays, CircleCheck, CircleX, Phone } from 'lucide-react';
import { Pause } from "@mui/icons-material";


const ConversionInsight = ({ conversionData, customerName, createdAt, dialerId, transcript }) => {
    const [audioSrc, setAudioSrc] = useState(null);
    const audioPath = process.env.REACT_APP_CONVOZE_AUDIO_PATH;
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    // const BAR_COUNT = 80;

    // const bars = Array.from({ length: BAR_COUNT }, (_, i) => ({
    //     height: Math.floor(Math.random() * 16) + 8 // random heights
    // }));
    const BAR_COUNT = 83;

    const bars = useMemo(
        () =>
            Array.from({ length: BAR_COUNT }, () => ({
                height: Math.floor(Math.random() * 28) + 12
            })),
        []
    );

    const progressPercent = (currentTime / duration) * 100 || 0;
    const activeBarIndex = Math.floor(
        (currentTime / duration) * BAR_COUNT
    );



    const handleBarSeek = (index) => {
        if (!audioRef.current) return;

        const seekTime = (index / BAR_COUNT) * duration;
        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
    };

    const handlePlayPause = () => {
        if (!audioRef.current) return;

        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }

        setIsPlaying(!isPlaying);
    };

    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const onLoadedMetadata = () => setDuration(audio.duration);
        const onTimeUpdate = () => setCurrentTime(audio.currentTime);
        const onEnded = () => {
            setIsPlaying(false);
            setCurrentTime(0);
        };

        audio.addEventListener("loadedmetadata", onLoadedMetadata);
        audio.addEventListener("timeupdate", onTimeUpdate);
        audio.addEventListener("ended", onEnded);

        return () => {
            audio.removeEventListener("loadedmetadata", onLoadedMetadata);
            audio.removeEventListener("timeupdate", onTimeUpdate);
            audio.removeEventListener("ended", onEnded);
        };
    }, [audioSrc]);

    const formatTime = (time = 0) => {
        const min = Math.floor(time / 60);
        const sec = Math.floor(time % 60);
        return `${min}:${sec.toString().padStart(2, "0")}`;
    };

    const handleSeek = (e) => {
        const audio = audioRef.current;
        if (!audio) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = rect.width;

        const seekTime = (clickX / width) * duration;
        audio.currentTime = seekTime;
        setCurrentTime(seekTime);
    };

    const handleWaveSeek = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const percent = clickX / rect.width;

        const seekTime = percent * duration;
        audioRef.current.currentTime = seekTime;
        setCurrentTime(seekTime);
    };


    // const activeBarIndex = Math.floor(
    //     (currentTime / duration) * BAR_COUNT
    // );

    useEffect(() => {
        if (transcript?.audio?.filePath) {
            setAudioSrc(`${audioPath}/${transcript.audio.filePath}`);
        }
    }, [transcript]);

    // Sample data for fields not in conversionData
    const customer = customerName;
    const callId = dialerId;
    const date = createdAt;
    // const time = "10:00 AM";
    // const duration = "00:30 / 01:00";

    console.log(customerName, "customerName", createdAt, "createdAt");
    const isConverted = conversionData.status === "Converted";
    const isFollowUp = conversionData.status === "Follow-up Required";


    const chipConfig = isConverted
        ? {
            icon: <CircleCheck size={14} strokeWidth={1.5} color="#2e7d32" />,
            color: "#2e7d32",
            border: "#2e7d32",
        }
        : isFollowUp
            ? {
                icon: <AlertCircle size={14} strokeWidth={1.5} color="#ed6c02" />,
                color: "#ed6c02",
                border: "#ff9800",
            }
            : {
                icon: <CircleX size={14} strokeWidth={1.5} color="#d32f2f" />,
                color: "#d32f2f",
                border: "#f44336",
            };



    // const isConverted = conversionData.status === "Converted";

    return (
        <Box sx={{ bgcolor: '#ffff', minHeight: '100vh' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="h5" fontWeight={700} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    Conversion Insight
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Analysis of the conversation outcome with actionable recommendations to
                    improve conversion when the call does not result in a successful outcome.
                </Typography>
            </Box>

            {/* Pipeline Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: "space-between",
                mb: 3,
                gap: 1.5,
                bgcolor: 'white',
                p: 2,
                borderRadius: 1,
                border: '1px solid #e0e0e0'
            }}>
                <Typography variant="body2" sx={{ color: '#5f6368', fontWeight: 500 }}>
                    Conversion Pipeline:
                </Typography>
                <Box sx={{ display: "flex", alignContent: "center", justifyContent: 'space-between', gap: '15px' }}>
                    {/* <Chip
                        icon={
                            isConverted ? (
                                <CircleCheck size={14} strokeWidth={1.5} color="#2e7d32" />
                            ) : (
                                <CircleX size={14} strokeWidth={1.5} color="#d32f2f" />
                            )
                        }
                        label={`OUTCOME: ${conversionData.outcome}`}
                        size="small"
                        sx={{
                            bgcolor: "transparent",
                            border: `1px solid ${isConverted ? "#2e7d32" : "#f44336"}`,
                            color: isConverted ? "#2e7d32" : "#d32f2f",
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            height: 24,
                            "& .MuiChip-icon": {
                                ml: "6px"
                            }
                        }}
                    /> */}
                    <Chip
                        icon={chipConfig.icon}
                        label={`OUTCOME: ${conversionData.outcome}`}
                        size="small"
                        sx={{
                            bgcolor: "transparent",
                            border: `1px solid ${chipConfig.border}`,
                            color: chipConfig.color,
                            fontWeight: 500,
                            fontSize: "0.75rem",
                            height: 24,
                            "& .MuiChip-icon": {
                                ml: "6px",
                            },
                        }}
                    />

                    <Typography sx={{ color: '#dadce0', mx: 0.5 }}>→</Typography>
                    <Chip
                        label={`CATEGORY: ${conversionData.responsible}`}
                        size="small"
                        sx={{
                            bgcolor: 'transparent',
                            border: '1px solid #dadce0',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            height: 24
                        }}
                    />
                    <Typography sx={{ color: '#dadce0', mx: 0.5 }}>→</Typography>
                    <Chip
                        label={`REASON: ${conversionData.reason}`}
                        size="small"
                        sx={{
                            bgcolor: 'transparent',
                            border: '1px solid #dadce0',
                            fontWeight: 500,
                            fontSize: '0.75rem',
                            height: 24
                        }}
                    />
                </Box>
            </Box>

            {/* Profile Bar with Audio Player */}
            <Card sx={{ mb: 3, border: "1px solid #e0e0e0" }}>
                <CardContent sx={{ py: 2 }}>
                    <Grid container alignItems="center" spacing={3}>
                        <Grid item xs={12} md={8}>
                            <Grid container alignItems="center" spacing={2} wrap="nowrap">
                                <Grid item>
                                    <Avatar
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            bgcolor: "#e8eaed",
                                            color: "#5f6368"
                                        }}
                                    >
                                        👤
                                    </Avatar>
                                </Grid>

                                <Grid item>
                                    <Typography sx={{ fontWeight: 600, fontSize: "0.9375rem" }}>
                                        {customer}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: "#5f6368",
                                            fontSize: "0.75rem",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 0.5
                                        }}
                                    >
                                        <Phone size={12} />
                                        {callId} •
                                        <CalendarDays size={12} />
                                        {date}
                                    </Typography>
                                </Grid>

                                <Grid item>
                                    {/* <Chip
                                        icon={
                                            isConverted ? (
                                                <CircleCheck size={14} strokeWidth={1.5} color="#2e7d32" />
                                            ) : (
                                                <CircleX size={14} strokeWidth={1.5} color="#d32f2f" />
                                            )
                                        }
                                        label={conversionData.status}
                                        size="small"
                                        sx={{
                                            bgcolor: "#fff",
                                            border: `1px solid ${isConverted ? "#2e7d32" : "#f44336"}`,
                                            color: isConverted ? "#2e7d32" : "#d32f2f",
                                            fontSize: "0.75rem",
                                            height: 24,
                                            "& .MuiChip-icon": { ml: "6px" }
                                        }}
                                    /> */}
                                    <Chip
                                        icon={chipConfig.icon}
                                        label={`${conversionData.outcome}`}
                                        size="small"
                                        sx={{
                                            bgcolor: "transparent",
                                            border: `1px solid ${chipConfig.border}`,
                                            color: chipConfig.color,
                                            fontWeight: 500,
                                            fontSize: "0.75rem",
                                            height: 24,
                                            "& .MuiChip-icon": {
                                                ml: "6px",
                                            },
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Audio Player */}
                        <Grid item xs={12} md={4}>
                            <Grid container alignItems="center"  wrap="nowrap" spacing={2} sx={{ display: "flex", alignItems: "center" }}>
                                <Grid item>
                                    <IconButton
                                        onClick={handlePlayPause}
                                        sx={{
                                            bgcolor: "#8e24aa",
                                            color: "white",
                                            width: 34,
                                            height: 34,
                                            "&:hover": { bgcolor: "#7b1fa2" },
                                            mb: "0.5rem"
                                        }}
                                    >
                                        {isPlaying ? <Pause /> : <PlayArrow />}
                                    </IconButton>

                                </Grid>

                                <Grid item xs>
                                    <Box sx={{ width: "100%" }}>
                                        {/* Progress line */}
                                        {/* <Box
                                            onClick={handleSeek}
                                            sx={{
                                                height: 6,
                                                width: "100%",
                                                bgcolor: "#e0e0e0",
                                                borderRadius: 3,
                                                cursor: "pointer",
                                                position: "relative"
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: "100%",
                                                    width: `${(currentTime / duration) * 100 || 0}%`,
                                                    bgcolor: "#8e24aa",
                                                    borderRadius: 3,
                                                    transition: "width 0.1s linear"
                                                }}
                                            />
                                        </Box> */}
                                        {/* 
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "flex-end",
                                                gap: "3px",
                                                height: 24,
                                                cursor: "pointer"
                                            }}
                                        >
                                            {bars.map((bar, index) => (
                                                <Box
                                                    key={index}
                                                    onClick={() => handleBarSeek(index)}
                                                    sx={{
                                                        width: 3,
                                                        height: bar.height,
                                                        bgcolor: index <= activeBarIndex ? "#8e24aa" : "#e0e0e0",
                                                        borderRadius: 1,
                                                        transition: "background-color 0.1s linear"
                                                    }}
                                                />
                                            ))}
                                        </Box> */}

                                        <Box
                                            onClick={handleWaveSeek}
                                            sx={{
                                                position: "relative",
                                                height: 32,
                                                display: "flex",
                                                alignItems: "flex-end",
                                                gap: "3px",
                                                cursor: "pointer"
                                            }}
                                        >
                                            {/* Bars */}
                                            {bars.map((bar, index) => (
                                                <Box
                                                    key={index}
                                                    sx={{
                                                        width: 3,
                                                        height: bar.height,
                                                        bgcolor: index <= activeBarIndex ? "#7b5cd6" : "#dcdcdc",
                                                        borderRadius: 1,
                                                        zIndex: 1
                                                    }}
                                                />
                                            ))}

                                            {/* Progress line */}
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    left: 0,
                                                    top: "50%",
                                                    height: 2,
                                                    width: `${progressPercent}%`,
                                                    bgcolor: "#7b5cd6",
                                                    transform: "translateY(-50%)",
                                                    zIndex: 2
                                                }}
                                            />

                                            {/* Remaining line */}
                                            <Box
                                                sx={{
                                                    position: "absolute",
                                                    right: 0,
                                                    top: "50%",
                                                    height: 2,
                                                    width: `${100 - progressPercent}%`,
                                                    bgcolor: "#e5e5e5",
                                                    transform: "translateY(-50%)",
                                                    zIndex: 0
                                                }}
                                            />
                                        </Box>


                                        {/* Time */}
                                        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5 }}>
                                            <Typography variant="caption">
                                                {formatTime(currentTime)}
                                            </Typography>
                                            <Typography variant="caption">
                                                {formatTime(duration)}
                                            </Typography>
                                        </Box>

                                        {/* Hidden audio */}
                                        <audio ref={audioRef} src={audioSrc} preload="metadata" />
                                    </Box>
                                </Grid>


                                <Grid item>
                                    {/* <Typography
                                        variant="caption"
                                        sx={{ color: "#5f6368", fontSize: "0.75rem", minWidth: 80 }}
                                    >
                                        {duration}
                                    </Typography> */}
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            <Grid container spacing={3}>
                {/* Left Column */}
                <Grid item xs={12} md={7}>
                    <Stack spacing={3}>
                        {/* Why this call didn't convert */}
                        <Card sx={{ border: '1px solid #e0e0e0' }}>
                            <CardContent>
                                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                    <InfoOutlined sx={{ fontSize: 18, color: '#000000ff' }} />
                                    <Typography variant="body2" sx={{ color: '#000000ff', fontWeight: 500 }}>
                                        Why this call didn't convert
                                    </Typography>
                                </Stack>
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem', mb: 2 }}>
                                    {conversionData.summary.title}
                                </Typography>
                                <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, fontSize: '0.875rem', color: '#3c4043' } }}>
                                    {conversionData.summary.description.map((desc, idx) => (
                                        <li key={idx}>{desc}</li>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>

                        {/* What went wrong */}
                        <Card sx={{ border: '1px solid #e0e0e0' }}>
                            <CardContent>
                                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                    <ErrorOutline sx={{ fontSize: 18, color: '#000000ff' }} />
                                    <Typography variant="body2" sx={{ color: '#000000ff', fontWeight: 500 }}>
                                        What went wrong
                                    </Typography>
                                </Stack>
                                <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1.5, fontSize: '0.875rem', color: '#3c4043' } }}>
                                    {conversionData.breakdown.map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Where the opportunity was missed */}
                        {/* <Card sx={{ border: '1px solid #e0e0e0' }}>
                            <CardContent>
                                <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                    <HistoryToggleOff sx={{ fontSize: 18, color: '#5f6368' }} />
                                    <Typography variant="body2" sx={{ color: '#5f6368', fontWeight: 500 }}>
                                        Where the opportunity was missed
                                    </Typography>
                                </Stack>
                                {conversionData.timeline.map((item, idx) => (
                                    <Box key={idx} sx={{ mb: 4, position: 'relative', pl: 7 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                position: 'absolute',
                                                left: 0,
                                                top: 0,
                                                color: '#5f6368',
                                                fontWeight: 500,
                                                fontSize: '0.75rem'
                                            }}
                                        >
                                            {item.time}
                                        </Typography>
                                        <Box sx={{
                                            borderLeft: '3px solid #e0e0e0',
                                            pl: 2,
                                            position: 'relative'
                                        }}>
                                            <Box sx={{
                                                position: 'absolute',
                                                left: -6,
                                                top: 8,
                                                width: 9,
                                                height: 9,
                                                borderRadius: '50%',
                                                bgcolor: item.isPositive ? '#4caf50' : '#e0e0e0'
                                            }} />

                                            <Box sx={{ mb: 1.5 }}>
                                                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#3c4043', mb: 0.5 }}>
                                                    <strong>Signal:</strong> {item.signal}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#3c4043' }}>
                                                    <strong>Response:</strong> {item.response}
                                                </Typography>
                                            </Box>

                                            <Box sx={{
                                                bgcolor: '#f1f3f4',
                                                p: 1.5,
                                                borderRadius: 1,
                                                borderLeft: '3px solid #5f6368'
                                            }}>
                                                <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: '#5f6368', fontStyle: 'italic' }}>
                                                    Missed Opportunity: {item.missedOpp}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </CardContent>
                        </Card> */}
                    </Stack>
                </Grid>

                {/* Right Column */}
                <Grid item xs={12} md={5}>
                    <Stack spacing={3}>
                        {/* How this call could have converted */}
                        <Card sx={{ border: '1px solid #e0e0e0', borderLeft: '4px solid #8e24aa' }}>
                            <CardContent>
                                <Typography variant="body2" sx={{ fontWeight: 600, mb: 2, fontSize: '0.875rem' }}>
                                    How this call could have converted
                                </Typography>
                                <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 2, '& li': { mb: 1, fontSize: '0.875rem', color: '#3c4043' } }}>
                                    {conversionData.coaching.text.map((tip, idx) => (
                                        <li key={idx}>{tip}</li>
                                    ))}
                                </Box>
                                <Box sx={{ bgcolor: '#f3e5f5', p: 2, borderRadius: 1, borderLeft: '3px solid #8e24aa' }}>
                                    <Typography variant="caption" sx={{ display: 'block', color: '#8e24aa', fontWeight: 600, mb: 1, fontSize: '0.75rem' }}>
                                        Try asking next time:
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', fontSize: '0.875rem', color: '#3c4043' }}>
                                        {conversionData.coaching.tip}
                                    </Typography>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Playbook for similar calls */}
                        <Card sx={{ border: '1px solid #e0e0e0' }}>
                            <CardContent>
                                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                    <MenuBook sx={{ fontSize: 18, color: '#000000ff' }} />
                                    <Typography variant="body2" sx={{ color: '#000000ff', fontWeight: 500 }}>
                                        Playbook for similar calls
                                    </Typography>
                                </Stack>
                                <Box component="ul" sx={{ pl: 2.5, m: 0, '& li': { mb: 1, fontSize: '0.875rem', color: '#3c4043' } }}>
                                    {conversionData.playbook.map((item, idx) => (
                                        <li key={idx}>{item}</li>
                                    ))}
                                </Box>
                            </CardContent>
                        </Card>

                        {/* Call evidence */}
                        {/* <Card sx={{ border: '1px solid #e0e0e0' }}>
                            <CardContent>
                                <Stack direction="row" spacing={1} alignItems="center" mb={2}>
                                    <ChatBubbleOutline sx={{ fontSize: 18, color: '#5f6368' }} />
                                    <Typography variant="body2" sx={{ color: '#5f6368', fontWeight: 500 }}>
                                        Call evidence
                                    </Typography>
                                </Stack>
                                <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 1 }}>
                                    <Stack spacing={2}>
                                        {conversionData.evidence.map((ev, idx) => (
                                            <Box key={idx}>
                                                <Typography variant="caption" sx={{ color: '#5f6368', fontSize: '0.75rem', mb: 0.5, display: 'block' }}>
                                                    {ev.speaker} {ev.time}
                                                </Typography>
                                                {ev.speaker === 'Agent' ? (
                                                    <Stack direction="row" spacing={1} alignItems="center">
                                                        <Avatar sx={{ width: 28, height: 28, bgcolor: '#8e24aa', fontSize: '0.75rem' }}>
                                                            {conversionData.agent[0]}
                                                        </Avatar>
                                                        <Box sx={{ bgcolor: 'white', p: 1.5, borderRadius: 1, border: '1px solid #e0e0e0', flexGrow: 1 }}>
                                                            <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#3c4043' }}>
                                                                {ev.text}
                                                            </Typography>
                                                        </Box>
                                                    </Stack>
                                                ) : (
                                                    <Box sx={{
                                                        bgcolor: ev.highlight ? '#fff9c4' : 'white',
                                                        p: 1.5,
                                                        borderRadius: 1,
                                                        border: ev.highlight ? '1px solid #f9f0a8' : '1px solid #e0e0e0'
                                                    }}>
                                                        <Typography variant="body2" sx={{ fontSize: '0.875rem', color: '#3c4043' }}>
                                                            {ev.text}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        ))}
                                    </Stack>
                                    <Box sx={{ textAlign: 'center', mt: 2 }}>
                                        <Typography
                                            variant="caption"
                                            sx={{
                                                color: '#8e24aa',
                                                fontSize: '0.75rem',
                                                fontWeight: 500,
                                                cursor: 'pointer',
                                                '&:hover': { textDecoration: 'underline' }
                                            }}
                                        >
                                            🔊 Jump to playback
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card> */}
                    </Stack>
                </Grid>
                <Grid item xs={12} md={12}>
                    <Card sx={{ border: '1px solid #e0e0e0' }}>
                        <CardContent>
                            <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                <HistoryToggleOff sx={{ fontSize: 18, color: '#5f6368' }} />
                                <Typography variant="body2" sx={{ color: '#000000ff', fontWeight: 500 }}>
                                    Where the opportunity was missed
                                </Typography>
                            </Stack>
                            {conversionData.timeline.map((item, idx) => (
                                <Box key={idx} sx={{ mb: 4, position: 'relative', pl: 7 }}>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            position: 'absolute',
                                            left: 0,
                                            top: 0,
                                            color: '#5f6368',
                                            fontWeight: 500,
                                            fontSize: '0.75rem'
                                        }}
                                    >
                                        {item.time}
                                    </Typography>
                                    <Box sx={{
                                        borderLeft: '3px solid #e0e0e0',
                                        pl: 2,
                                        position: 'relative'
                                    }}>
                                        <Box sx={{
                                            position: 'absolute',
                                            left: -6,
                                            top: 8,
                                            width: 9,
                                            height: 9,
                                            borderRadius: '50%',
                                            bgcolor: item.isPositive ? '#4caf50' : '#e0e0e0'
                                        }} />

                                        <Box sx={{ mb: 1.5 }}>
                                            <Typography variant="body2" sx={{ fontSize: '1.0586rem', color: '#3c4043', mb: 0.5 }}>
                                                <strong>
                                                    {item?.signal?.role ? `${item.signal.role}:` : 'Signal:'}
                                                </strong>{' '}
                                                {item?.signal?.text ?? item?.signal}

                                            </Typography>
                                            <Typography variant="body2" sx={{ fontSize: '1.0586rem', color: '#3c4043' }}>
                                                <strong>
                                                    {item?.response?.role ? `${item.response.role}:` : 'Response:'}
                                                </strong>{' '}
                                                {item?.response?.text ?? item?.response}

                                            </Typography>
                                        </Box>

                                        <Box sx={{
                                            bgcolor: '#ed9090ff',
                                            p: 1.5,
                                            borderRadius: 1,
                                            borderLeft: '3px solid #685f5fff'
                                        }}>
                                            <Typography variant="body2" sx={{ fontSize: '1.0586rem', color: '#000000ff', fontStyle: 'Bold' }}>
                                                Missed Opportunity: {item.missedOpp}
                                            </Typography>

                                        </Box>
                                        <Box sx={{
                                            mt: "3px",
                                            bgcolor: '#c8e6c9',
                                            p: 1.5,
                                            borderRadius: 1,
                                            borderLeft: '3px solid #5f6368'
                                        }}>
                                            <Typography variant="body2" sx={{ fontSize: '1.0586rem', color: '#000000ff', fontStyle: 'Bold' }}>
                                                Suggestion: {item.suggestion}
                                            </Typography>

                                        </Box>
                                    </Box>
                                </Box>
                            ))}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default ConversionInsight;